import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Menu,
  Grid,
} from "@mui/material";
import React, { useContext, useState, useEffect } from "react";
import InfinityCard from "./InfinityCard";
import CharacterCard from "./CharacterCard";
import CSVUploader from "./CSVUploader";
import EditCardModal from "./EditCardModal";
import { MainContext } from "../App";
import { db } from "../@shared/firebase/config";

const infinityAreas = [
  "Health",
  "Defense",
  "Attack",
  "Accuracy",
  "Turn Loss",
  "Player Hand",
  "Player Position",
  "All Stats",
];
const turnsActiveOptions = [
  "Instant",
  "1 Turn",
  "2 Turns",
  "3 Turns",
  "Instant + 1 Turn",
  "2 Turns / 1 Turn",
];
const rarityOptions = [
  { type: "Common", amt: 18 },
  { type: "Uncommon", amt: 19 },
  { type: "Rare", amt: 21 },
  { type: "Epic", amt: 23 },
  { type: "Legendary", amt: 25 },
];

const CardManager = () => {
  const { setError, setSuccess, setMessage } = useContext(MainContext);
  const [cardType, setCardType] = useState("");

  // Infinity (CSV/manual) card fields state
  const [infinityName, setInfinityName] = useState("");
  const [infinityDescription, setInfinityDescription] = useState("");
  const [infinityEffect, setInfinityEffect] = useState("");
  const [infinityAreaAffected, setInfinityAreaAffected] = useState([]);
  const [infinityTurnsActive, setInfinityTurnsActive] = useState("");
  const [infinityMax, setInfinityMax] = useState("");
  const [infinityFinite, setInfinityFinite] = useState(false);
  const [infinityCraftNeeded, setInfinityCraftNeeded] = useState("");
  const [infinityCraftAmount, setInfinityCraftAmount] = useState("");
  const [infinityAvailableForPurchase, setInfinityAvailableForPurchase] =
    useState(false);
  const [infinityPrice, setInfinityPrice] = useState("");
  const [infinitySeason, setInfinitySeason] = useState("");
  const [infinityImageUrl, setInfinityImageUrl] = useState("");
  const [infinityBgColor1, setInfinityBgColor1] = useState("");
  const [infinityBgColor2, setInfinityBgColor2] = useState("");

  // Character card fields state
  const [characterName, setCharacterName] = useState("");
  const [attackName, setAttackName] = useState("");
  const [characterRarity, setCharacterRarity] = useState("");
  const [characterDescription, setCharacterDescription] = useState("");
  const [characterCollectionName, setCharacterCollectionName] = useState("");
  const [characterTokenId, setCharacterTokenId] = useState("");
  const [characterAttack, setCharacterAttack] = useState("");
  const [characterAccuracy, setCharacterAccuracy] = useState("");
  const [characterDefense, setCharacterDefense] = useState("");
  const [characterHealth, setCharacterHealth] = useState("");
  const [characterFinite, setCharacterFinite] = useState(false);
  const [characterAvailableForPurchase, setCharacterAvailableForPurchase] =
    useState(false);
  const [characterPrice, setCharacterPrice] = useState("");
  const [characterSeason, setCharacterSeason] = useState("");
  const [characterImageUrl, setCharacterImageUrl] = useState("");
  const [characterBgColor1, setCharacterBgColor1] = useState("");
  const [characterBgColor2, setCharacterBgColor2] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [charMenuOpen, setCharMenuOpen] = useState(false);
  const [infinityMenuOpen, setInfinityMenuOpen] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadedCards, setUploadedCards] = useState([]);

  // State for editing CSV-uploaded cards
  const [editingCard, setEditingCard] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // --- CSV Cancel/Submit Handlers ---
  const handleCancelCSV = () => {
    setUploadedCards([]);
    setCardType("");
    setMessage("CSV upload cancelled.");
  };

  const handleUploadCard = async (type) => {
    let cardData =
      type === "character"
        ? {
            name: characterName,
            attackName: attackName,
            rarity: characterRarity,
            description: characterDescription,
            collectionName: characterCollectionName,
            tokenId: characterTokenId,
            attack: characterAttack,
            accuracy: characterAccuracy,
            defense: characterDefense,
            health: characterHealth,
            finite: characterFinite,
            availableForPurchase: characterAvailableForPurchase,
            price: characterPrice,
            season: characterSeason,
            imageUrl: characterImageUrl,
            bgColor1: characterBgColor1,
            bgColor2: characterBgColor2,
          }
        : {
            name: infinityName,
            description: infinityDescription,
            effect: infinityEffect,
            areaAffected: infinityAreaAffected,
            turnsActive: infinityTurnsActive,
            max: infinityMax,
            finite: infinityFinite,
            craftNeeded: infinityCraftNeeded,
            craftAmount: infinityCraftAmount,
            availForPurchase: infinityAvailableForPurchase,
            price: infinityPrice,
            season: infinitySeason,
            imageUrl: infinityImageUrl,
            bgColor1: infinityBgColor1,
            bgColor2: infinityBgColor2,
          };

    try {
      // Determine collection based on type.
      const collectionName =
        type === "character" ? "Character cards" : "Infinity cards";
      await db.collection(collectionName).add(cardData);
      setSuccess(true);
      setMessage("Card uploaded successfully.");
      // Optionally clear any manual entry state here.
    } catch (error) {
      console.error("Firebase Upload Error:", error);
      setError(true);
      setMessage("Error uploading card to Firebase.");
    }
  };

  // Updated batch upload function that writes to the proper collection
  const handleUploadCards = async (cardsArray, type) => {
    try {
      const batch = db.batch();
      const collectionName =
        type === "character-csv" ? "Character cards" : "Infinity cards";
      const collectionRef = db.collection(collectionName);
      cardsArray.forEach((card) => {
        const docRef = collectionRef.doc();
        batch.set(docRef, card);
      });
      await batch.commit();
      setSuccess(true);
      setMessage("Cards uploaded successfully.");
      setUploadedCards([]);
      setCardType("");
    } catch (error) {
      console.error("Firebase Upload Error:", error);
      setError(true);
      setMessage("Error uploading cards to Firebase.");
    }
  };

  // --- Menu Handlers ---
  const handleClick = (event, type) => {
    setAnchorEl(event.currentTarget);
    if (type === "character") {
      setCharMenuOpen(true);
    } else if (type === "infinity") {
      setInfinityMenuOpen(true);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setCharMenuOpen(false);
    setInfinityMenuOpen(false);
  };

  // --- CSV Upload Handler ---
  // Updated to accept a second parameter 'type'
  const handleUploadCSV = async (newCards, type) => {
    try {
      setUploading(true);
      newCards = newCards
        .map((event) => {
          let cleanedCards = {};
          Object.keys(event).forEach((key) => {
            cleanedCards[key.trim()] = event[key];
          });
          return cleanedCards;
        })
        .map((event) => {
          return transformCardData(event);
        });
      // If a card has an "areaAffected" field, split it into an array.
      newCards = newCards.map((card) => {
        return {
          ...card,
          areaAffected: card.areaAffected ? card.areaAffected.split(",") : [],
        };
      });
      console.log("NEW CARDS => ", newCards);
      setUploadedCards(newCards);
      // Set cardType based on type from CSVUploader
      setCardType(type);
    } catch (error) {
      console.error("Upload Error:", error);
      setError(true);
      setMessage("Error processing events.");
    }
    setUploading(false);
  };

  const toCamelCase = (str) => {
    const formattedKey = str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/\s(.)/g, (match) => match.toUpperCase())
      .replace(/\s/g, "")
      .replace(/^(.)/, (match) => match.toLowerCase());
    return formattedKey;
  };

  const transformCardData = (event) => {
    const transformedCard = {};
    for (const key in event) {
      const newKey = toCamelCase(key);
      transformedCard[newKey] = event[key];
    }
    return transformedCard;
  };

  useEffect(() => {}, [uploadedCards]);

  return (
    <Box display="flex" height="100%">
      {/* Left Section */}
      <Box
        width="40vw"
        bgcolor="white"
        p={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        overflow="auto"
      >
        {cardType === "" ? (
          <>
            <Button
              variant="outlined"
              id="character-btn"
              aria-controls={charMenuOpen ? "character-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={charMenuOpen ? "true" : undefined}
              onClick={(e) => handleClick(e, "character")}
              style={{ marginBottom: "8px", width: "80%" }}
              size="small"
            >
              Character Card
            </Button>
            <Menu
              id="character-menu"
              aria-labelledby="character-btn"
              anchorEl={anchorEl}
              open={charMenuOpen}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  setCardType("character");
                  handleClose();
                }}
              >
                Create Character Card
              </MenuItem>
              <MenuItem>
                <CSVUploader
                  onUpload={handleUploadCSV}
                  setCardType={setCardType}
                  type={"character-csv"}
                  close={handleClose}
                />
              </MenuItem>
            </Menu>

            <Button
              variant="outlined"
              id="infinity-btn"
              aria-controls={infinityMenuOpen ? "infinity-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={infinityMenuOpen ? "true" : undefined}
              onClick={(e) => handleClick(e, "infinity")}
              style={{ marginBottom: "8px", width: "80%" }}
              size="small"
            >
              Infinity Card
            </Button>
            <Menu
              id="infinity-menu"
              aria-labelledby="infinity-btn"
              anchorEl={anchorEl}
              open={infinityMenuOpen}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  setCardType("infinity");
                  handleClose();
                }}
              >
                Create Infinity Card
              </MenuItem>
              <MenuItem>
                <CSVUploader
                  onUpload={handleUploadCSV}
                  setCardType={setCardType}
                  type={"infinity-csv"}
                  close={handleClose}
                />
              </MenuItem>
            </Menu>
          </>
        ) : cardType === "infinity" ? (
          <>
            {/* Infinity Card Manual Entry Fields */}
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityName}
              onChange={(e) => setInfinityName(e.target.value)}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityDescription}
              onChange={(e) => setInfinityDescription(e.target.value)}
            />
            <TextField
              label="Effect"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityEffect}
              onChange={(e) => setInfinityEffect(e.target.value)}
            />
            <Box display="flex" width="100%" gap="8px">
              <FormControl
                fullWidth
                margin="dense"
                size="small"
                variant="outlined"
              >
                <InputLabel id="area-affected-label">Area Affected</InputLabel>
                <Select
                  labelId="area-affected-label"
                  multiple
                  value={infinityAreaAffected}
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setInfinityAreaAffected(
                      typeof value === "string" ? value.split(",") : value
                    );
                  }}
                  input={<OutlinedInput label="Area Affected" />}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {infinityAreas.map((area) => (
                    <MenuItem key={area} value={area}>
                      <Checkbox
                        checked={infinityAreaAffected.indexOf(area) > -1}
                      />
                      <ListItemText primary={area} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                margin="dense"
                size="small"
                variant="outlined"
              >
                <InputLabel id="turns-active-label">Turns Active</InputLabel>
                <Select
                  labelId="turns-active-label"
                  value={infinityTurnsActive}
                  onChange={(e) => setInfinityTurnsActive(e.target.value)}
                  label="Turns Active"
                >
                  {turnsActiveOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <TextField
                label="Max"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityMax}
                onChange={(e) => setInfinityMax(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={infinityFinite}
                    onChange={(e) => setInfinityFinite(e.target.checked)}
                    size="small"
                  />
                }
                label="Finite"
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Craft Needed"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                value={infinityCraftNeeded}
                onChange={(e) => setInfinityCraftNeeded(e.target.value)}
              />
              <TextField
                label="Craft Amount"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityCraftAmount}
                onChange={(e) => setInfinityCraftAmount(e.target.value)}
              />
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={infinityAvailableForPurchase}
                    onChange={(e) =>
                      setInfinityAvailableForPurchase(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Available for Purchase"
              />
              <TextField
                label="Price"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityPrice}
                onChange={(e) => setInfinityPrice(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Season"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={infinitySeason}
                onChange={(e) => setInfinitySeason(e.target.value)}
                style={{ width: "30%" }}
              />
              <TextField
                label="Image URL"
                variant="outlined"
                margin="dense"
                size="small"
                value={infinityImageUrl}
                onChange={(e) => setInfinityImageUrl(e.target.value)}
                style={{ width: "70%" }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px" mt={1}>
              <input
                type="color"
                value={infinityBgColor1}
                onChange={(e) => setInfinityBgColor1(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={infinityBgColor2}
                onChange={(e) => setInfinityBgColor2(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCardType("")}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUploadCard(cardType)}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Submit
            </Button>
          </>
        ) : cardType === "character" ? (
          <>
            {/* Character Card Manual Entry Fields */}
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Name"
                variant="outlined"
                margin="dense"
                size="small"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                style={{ flex: 2 }}
              />
              <FormControl
                margin="dense"
                variant="outlined"
                size="small"
                style={{ flex: 1 }}
              >
                <InputLabel id="rarity-label">Rarity</InputLabel>
                <Select
                  labelId="rarity-label"
                  value={characterRarity}
                  onChange={(e) => setCharacterRarity(e.target.value)}
                  label="Rarity"
                >
                  {rarityOptions.map((option) => (
                    <MenuItem key={option.type} value={option.type}>
                      {option.type} - {option.amt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              multiline
              rows={3}
              value={characterDescription}
              onChange={(e) => setCharacterDescription(e.target.value)}
            />
            <TextField
              label="attack name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              multiline
              rows={3}
              value={attackName}
              onChange={(e) => setAttackName(e.target.value)}
            />
            <TextField
              label="Collection Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={characterCollectionName}
              onChange={(e) => setCharacterCollectionName(e.target.value)}
            />
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Token Id"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterTokenId}
                onChange={(e) => setCharacterTokenId(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Attack"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterAttack}
                onChange={(e) => setCharacterAttack(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Accuracy"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterAccuracy}
                onChange={(e) => setCharacterAccuracy(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Defense"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterDefense}
                onChange={(e) => setCharacterDefense(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Health"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterHealth}
                onChange={(e) => setCharacterHealth(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={characterFinite}
                    onChange={(e) => setCharacterFinite(e.target.checked)}
                    size="small"
                  />
                }
                label="Finite"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={characterAvailableForPurchase}
                    onChange={(e) =>
                      setCharacterAvailableForPurchase(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Available for Purchase"
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Price"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterPrice}
                onChange={(e) => setCharacterPrice(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Season"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterSeason}
                onChange={(e) => setCharacterSeason(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Image URL"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={characterImageUrl}
              onChange={(e) => setCharacterImageUrl(e.target.value)}
            />
            <Box display="flex" width="100%" gap="8px" mt={1}>
              <input
                type="color"
                value={characterBgColor1}
                onChange={(e) => setCharacterBgColor1(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={characterBgColor2}
                onChange={(e) => setCharacterBgColor2(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCardType("")}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUploadCard(cardType)}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Submit
            </Button>
          </>
        ) : cardType === "infinity-csv" || cardType === "character-csv" ? (
          // CSV Upload Process Branch for both types
          <Box display="flex" flexDirection="column" alignItems="center">
            <p style={{ marginBottom: "16px" }}>
              What would you like to do next?
            </p>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              gap="16px"
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelCSV}
                size="medium"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleUploadCards(uploadedCards, cardType)}
                size="medium"
              >
                Submit
              </Button>
            </Box>
          </Box>
        ) : (
          <div>Select a card type to begin</div>
        )}
      </Box>

      {/* Right Section */}
      <Box width="60vw" bgcolor="lightgray" p={2}>
        {cardType === "character" ? (
          <Grid
            container
            item
            xs={12}
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
              height: "100%",
            }}
          >
            <CharacterCard
              name={characterName}
              attackName={attackName}
              rarity={characterRarity}
              description={characterDescription}
              collectionName={characterCollectionName}
              tokenId={characterTokenId}
              attack={characterAttack}
              accuracy={characterAccuracy}
              defense={characterDefense}
              health={characterHealth}
              finite={characterFinite}
              availableForPurchase={characterAvailableForPurchase}
              price={characterPrice}
              season={characterSeason}
              imageUrl={characterImageUrl}
              bgColor1={characterBgColor1 || "#fff"}
              bgColor2={characterBgColor2 || "#fff"}
              heightPer={80}
            />
          </Grid>
        ) : cardType === "character-csv" ? (
          <Grid
            container
            item
            xs={12}
            style={{ maxHeight: "100%", overflowY: "scroll" }}
          >
            {uploadedCards.map((card, index) => (
              <Grid item xs={12} md={3} key={index}>
                <CharacterCard
                  characterName={card.name}
                  attackName={card.attackName}
                  characterRarity={card.rarity}
                  characterDescription={card.description}
                  characterCollectionName={card.collectionName}
                  characterTokenId={card.tokenId}
                  characterAttack={card.attack}
                  characterAccuracy={card.accuracy}
                  characterDefense={card.defense}
                  characterHealth={card.health}
                  characterFinite={card.finite}
                  characterAvailableForPurchase={card.availForPurchase}
                  characterPrice={card.price}
                  characterSeason={card.season}
                  characterImageUrl={card.imageUrl}
                  pho={card.bgColor1 || "#fff"}
                  characterBgColor2={card.bgColor2 || "#fff"}
                  // Optionally pass heightPer and borderPx if needed:
                  heightPer={80}
                  borderPx={18}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setEditingCard(card);
                    setEditModalOpen(true);
                  }}
                  style={{ marginTop: "8px" }}
                >
                  Edit This
                </Button>
              </Grid>
            ))}
          </Grid>
        ) : cardType === "infinity" ? (
          <Grid
            container
            item
            xs={12}
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
              height: "100%",
            }}
          >
            <InfinityCard
              name={infinityName}
              description={infinityDescription}
              effect={infinityEffect}
              areaAffected={infinityAreaAffected}
              turnsActive={infinityTurnsActive}
              max={infinityMax}
              finite={infinityFinite}
              craftNeeded={infinityCraftNeeded}
              craftAmount={infinityCraftAmount}
              availableForPurchase={infinityAvailableForPurchase}
              price={infinityPrice}
              season={infinitySeason}
              bgColor1={infinityBgColor1}
              bgColor2={infinityBgColor2}
              imageUrl={infinityImageUrl}
              heightPer={80}
              borderPx={18}
            />
          </Grid>
        ) : cardType === "infinity-csv" ? (
          <Grid
            container
            item
            xs={12}
            style={{ maxHeight: "100%", overflowY: "scroll" }}
          >
            {uploadedCards.map((card, index) => (
              <Grid
                item
                container
                xs={12}
                md={4}
                key={index}
                style={{ height: "30%" }}
              >
                <Box style={{ width: "100%", aspectRatio: "2.5/3.5" }}>
                  <InfinityCard
                    name={card.name}
                    description={card.description}
                    effect={card.effect}
                    areaAffected={card.areaAffected}
                    turnsActive={card.turnsActive}
                    max={card.max}
                    finite={card.finite}
                    craftNeeded={card.craftNeeded}
                    craftAmount={card.craftAmount}
                    availableForPurchase={card.availForPurchase}
                    price={card.price}
                    season={card.season}
                    bgColor1={card.bgColor1 || "#fff"}
                    bgColor2={card.bgColor2 || "#fff"}
                    imageUrl={card.imageUrl}
                    heightPer={40}
                    borderPx={10}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setEditingCard(card);
                    setEditModalOpen(true);
                  }}
                  style={{ marginTop: "8px" }}
                >
                  Edit
                </Button>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div>Select a card type to begin</div>
        )}
      </Box>

      {/* Edit Modal for CSV-uploaded Infinity Cards */}
      {editModalOpen && editingCard && (
        <EditCardModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          card={editingCard}
          onUpdate={(updatedCard) => {
            let cardsCopy = [...uploadedCards];
            const index = cardsCopy.findIndex(
              (c) => c.name === editingCard.name
            );
            cardsCopy[index] = updatedCard;
            console.log("WE ARE UPDATING CARDS", cardsCopy);
            setUploadedCards(cardsCopy);

            setEditingCard(null);
            setEditModalOpen(false);
          }}
        />
      )}
    </Box>
  );
};

export default CardManager;
